import { useEffect, useRef, useState } from 'react';
import { useAdobeImsAuth } from './auth/useAdobeImsAuth';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { FilterBar } from './components/FilterBar';
import { VPPanel } from './components/panels/VPPanel';
import { SDPanel } from './components/panels/SDPanel';
import { ODPanel } from './components/panels/ODPanel';
import { AvailabilityTrackerPanel } from './components/panels/AvailabilityTrackerPanel';
import { HeatmapModal } from './components/HeatmapModal';
import { UploadCsvModal } from './components/UploadCsvModal';
import { AiChatSidebar } from './components/AiChatSidebar';
import { useAppState } from './hooks/useAppState';
import { DealsProvider, useDeals } from './context/DealsContext';
import { OrgProvider } from './context/OrgContext';
import { fetchDeals, fetchTouchpoints, triggerSync } from './utils/database';
import { friendlyTime } from './utils/friendlyTime';
import { getLatestFY } from './utils/filter';
import type { TabState } from './types';
function Spinner({
  size = 16,
  color = '#6B7280'
}: {
  size?: number;
  color?: string;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" className="animate-spin" style={{
    color
  }} data-id="element-0" data-mp="3mjg9vlcoy9m7">
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" data-id="element-1" data-mp="3recdzjv7cnfo" />
      <path d="M12 2a10 10 0 0 1 10 10" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" data-id="element-2" data-mp="7svdij4jt6t" />
    </svg>;
}
const ADOBE_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png';

// Inner component has access to DealsProvider context
function Dashboard() {
  const {
    accessLevel,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const {
    state,
    currentTab,
    updateTab,
    switchTab,
    drillRegion
  } = useAppState();
  const {
    deals,
    setDeals,
    touchpoints,
    setTouchpoints,
    setSyncMeta,
    syncStatus,
    lastSynced,
    isLiveData
  } = useDeals();

  // Default the FY filter to the latest fiscal year present in the data, once, the first
  // time real data loads — across all three tabs. Doesn't override a later manual choice.
  const hasAutoSelectedFY = useRef(false);
  useEffect(() => {
    if (hasAutoSelectedFY.current || deals.length === 0) return;
    const latestFY = getLatestFY(deals);
    if (!latestFY) return;
    hasAutoSelectedFY.current = true;
    updateTab('vp', {
      fy: latestFY
    });
    updateTab('sd', {
      fy: latestFY
    });
    updateTab('od', {
      fy: latestFY
    });
  }, [deals, updateTab]);
  const [syncError, setSyncErrorMsg] = useState<string | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [showUploadCsv, setShowUploadCsv] = useState(false);
  const [showAiChat, setShowAiChat] = useState(false);
  const canSync = accessLevel === 'editor' || accessLevel === 'admin';
  const refreshDeals = () => {
    setSyncMeta('loading');

    // Fetch deals (blocks the loader) and touchpoints (background, non-blocking) in parallel.
    // The loader only waits for fetchDeals to complete.
    fetchDeals().then(result => {
      if (result && result.records.length > 0) {
        setDeals(result.records);
        setSyncMeta(result.stale ? 'error' : 'success', result.lastSyncedAt ?? undefined);
      } else {
        setSyncMeta('never');
      }
    }).catch(err => {
      console.error('fetchDeals failed:', err);
      setSyncMeta('error');
    });

    // Touchpoints fetch runs in the background — does not block the loader.
    fetchTouchpoints().then(result => {
      console.log('[App] fetchTouchpoints result:', result?.records?.length ?? 0, 'records');
      if (result && result.records.length > 0) {
        setTouchpoints(result.records);
      }
    }).catch(err => {
      console.error('fetchTouchpoints failed:', err);
    });
  };

  // Fetch live deal data once the user is authenticated and provisioned
  useEffect(() => {
    if (!isSignedIn || accessLevel === 'unregistered' || accessLevel === 'anonymous') return;
    void refreshDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, accessLevel]);
  const handleManualSync = async () => {
    setSyncMeta('loading');
    setSyncErrorMsg(null);
    try {
      // Trigger both reports in parallel
      const results = await Promise.allSettled([triggerSync('all-ondemand-global-requests'), triggerSync('all-ondemand-global-touchpoints')]);

      // Check if at least one succeeded
      const anySucceeded = results.some(r => r.status === 'fulfilled' && r.value);
      if (anySucceeded) {
        // Refresh data from both reports
        void refreshDeals();
      } else {
        // Both failed
        const firstError = results.find(r => r.status === 'rejected');
        const msg = firstError && firstError.status === 'rejected' ? firstError.reason instanceof Error ? firstError.reason.message : 'Sync failed' : 'Sync failed';
        setSyncErrorMsg(msg);
        setSyncMeta('error');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Sync failed';
      setSyncErrorMsg(msg);
      setSyncMeta('error');
    }
  };
  if (!isAuthReady || isAuthLoading) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-3" data-mp="2eu4vr3xrvlbt">
        Initializing Adobe sign-in...
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-4" data-mp="2nwwapdpuwz87">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-5" data-mp="2stwh5pqmc3zu">
          <div className="flex items-center gap-4" data-id="element-6" data-mp="tjek1hu5nm8y">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-7" data-mp="yg4yy1u84bss" />
            <div data-id="element-8" data-mp="21rao9w7wbp1b">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-9" data-mp="21qskmeiwm8jd">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-10" data-mp="1wt0qdduseac0">Sign in with Adobe ID</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-11" data-mp="2atbjyonhe30v">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-12" data-mp="2aq5v9airl35o">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-13" data-mp="2kovfh9y4uzp3" /><br data-id="element-14" data-mp="2kolnxhxfwkha" />
              <p data-id="element-15" data-mp="2ko3hjsh2cpvn">
                1. Click Sign-In<br data-id="element-16" data-mp="2frv5iny5xcsp" />
                2. Login using your company account<br data-id="element-17" data-mp="2fp912x6kjdep" />
                3. Choose profile: Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-18" data-mp="2pgmv4pfr3l0g">
                {authError}
              </div>}

            <button onClick={() => void signIn()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl bg-red-600 px-6 py-3 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 transition-colors" data-id="element-19" data-mp="33eti3aj5w0lc">
              {isAuthLoading ? 'Signing in…' : 'Sign in with Adobe ID'}
            </button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-20" data-mp="3d6czypr1nl9g">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-21" data-mp="343lmkkelyff6">
          <div className="flex items-center gap-4" data-id="element-22" data-mp="2pf7gm0lwaaue">
            <img src={ADOBE_LOGO_URL} alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-23" data-mp="2pgsfg2f8sfy2" />
            <div data-id="element-24" data-mp="2uetumc9jrv49">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-25" data-mp="2uebvn7vi32sn">OnDemand Team Dashboard</h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-26" data-mp="2gf2nmvhg7ezr">Account not provisioned</p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-27" data-mp="2khp8gcfpbzng">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-28" data-mp="30vndk73ovof1">
              You are signed in with Adobe ID, but this account is not registered for dashboard
              access. No data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-29" data-mp="2m07chi4oys95">
                {authError}
              </div>}

            <button onClick={() => void signOut()} disabled={isAuthLoading} className="w-full flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-6 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors" data-id="element-30" data-mp="2d515iftoq451">
              {isAuthLoading ? 'Signing out…' : 'Sign out'}
            </button>
          </div>
        </div>
      </div>;
  }
  return <OrgProvider data-id="element-31" data-mp="9pny0oz4efwg">
    <div className="min-h-screen" data-id="element-32" data-mp="9o4hqydgs4uo">
      <Header user={user} accessLevel={accessLevel} isAuthLoading={isAuthLoading} onSignOut={() => void signOut()} onToggleAiChat={() => setShowAiChat(v => !v)} isAiChatOpen={showAiChat} data-id="element-33" data-mp="9r8uwl8wqaxh" />

      {/* Sync status bar — visible to all; sync button only for editor/admin */}
      <div className="flex items-center gap-3 px-4 py-1.5 bg-slate-50 border-b border-slate-200 text-xs text-slate-500" data-id="element-34" data-mp="no5jsh5gtibq">
        <span className="flex items-center gap-1.5" data-id="element-35" data-mp="3m486yxmz2kua">
          {syncStatus === 'loading' && <><Spinner size={12} data-id="element-36" data-mp="3r5nr8nhbsnbm" /><span data-id="element-37" data-mp="3r31jamqiw5sv">Loading live data…</span></>}
          {syncStatus === 'success' && `✓ Data synced${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'error' && `⚠ Showing deals cached ${lastSynced ? ` · ${friendlyTime(lastSynced)}` : ''}`}
          {syncStatus === 'never' && '— No live data yet; showing sample data'}
          {syncStatus === 'idle' && ''}
        </span>
        {syncError && <span className="text-red-600 ml-2" data-id="element-38" data-mp="2f7dkynnr32n8">{syncError}</span>}
        <div className="ml-auto flex items-center gap-2" data-id="element-39" data-mp="2f6vivaeepu7e">
          {isLiveData && accessLevel === 'admin' && <button onClick={() => setShowHeatmap(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-40" data-mp="2k81at608tdfv">
              Heatmap
            </button>}
          {isLiveData && (accessLevel === 'admin' || accessLevel === 'editor') && <button onClick={() => setShowUploadCsv(true)} className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-41" data-mp="21gi9d3lva7fy">
              Upload CSV
            </button>}
          {canSync && <button onClick={() => void handleManualSync()} disabled={syncStatus === 'loading'} className="px-3 py-1 rounded-md bg-slate-700 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-50 transition-colors flex items-center gap-1.5" data-id="element-42" data-mp="1dpctwpju9h66">
              {syncStatus === 'loading' ? <><Spinner size={12} color="#fff" data-id="element-43" data-mp="3mykbnadtnc1l" />Syncing…</> : 'Sync data'}
            </button>}
        </div>
      </div>

      {showHeatmap && <HeatmapModal deals={deals} onClose={() => setShowHeatmap(false)} data-id="element-44" data-mp="3i1tusku70cf4" />}

      {showUploadCsv && <UploadCsvModal onClose={() => setShowUploadCsv(false)} onUploaded={() => void refreshDeals()} data-id="element-45" data-mp="3d2hdefm3bxjx" />}

      {showAiChat && <AiChatSidebar onClose={() => setShowAiChat(false)} data-id="element-46" data-mp="347jgyyjqi2nu" />}

      <TabNav currentTab={currentTab} onSwitch={switchTab} data-id="element-47" data-mp="2z987ioxcpu65" />

      <div className="relative" data-id="element-48" data-mp="2zb2r9zwvzg8n">
        {/* Initial page-load overlay — shown while data is being fetched for the first time */}
        {syncStatus === 'loading' && !isLiveData && <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white/80 min-h-64" data-id="element-49" data-mp="2pcdakagq81tz">
            <Spinner size={36} color="#4F46E5" data-id="element-50" data-mp="2fm4zz51i071q" />
            <p className="text-sm text-slate-500" data-id="element-51" data-mp="2kn2gld6v0t14">Loading dashboard data…</p>
          </div>}

        {currentTab === 'vp' && <>
            <FilterBar tab="vp" state={state.vp} onUpdate={u => updateTab('vp', u as Partial<TabState>)} data-id="element-52" data-mp="26oun5f9189up" />
            <VPPanel state={state.vp} onDrillRegion={drillRegion} data-id="element-53" data-mp="2aq6fhsjaj96l" />
          </>}
        {currentTab === 'sd' && <>
            <FilterBar tab="sd" state={state.sd} onUpdate={u => updateTab('sd', u as Partial<TabState>)} data-id="element-54" data-mp="33dabj80eod68" />
            <SDPanel state={state.sd} data-id="element-55" data-mp="2pkkpj3kvn894" />
          </>}
        {currentTab === 'od' && <>
            <FilterBar tab="od" state={state.od} onUpdate={u => updateTab('od', u as Partial<TabState>)} data-id="element-56" data-mp="2tlwi9hz1s9lj" />
            <ODPanel state={state.od} data-id="element-57" data-mp="3hb8picate7l7" />
          </>}
        {currentTab === 'at' && <AvailabilityTrackerPanel data-id="element-58" data-mp="3m4jwcdfmmod4" />}
      </div>
    </div>
    </OrgProvider>;
}
export default function App() {
  return <DealsProvider data-id="element-59" data-mp="1amq9t7ozoalj">
      <Dashboard data-id="element-60" data-mp="1am6pxlfs3c4v" />
    </DealsProvider>;
}